import React, { useState, useEffect, useCallback, useMemo } from 'react';
import styled, { keyframes } from 'styled-components';
import { createPortal } from 'react-dom';
import { theme } from '../theme';
import ScrollReveal from './ScrollReveal';

const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: scale(0.95);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
`;

const CertificatesSection = styled.section`
  padding: 6rem 2.5rem;
  background: ${theme.colors.neutral[50]};
  background-image: 
    radial-gradient(at 0% 0%, ${theme.colors.accent[50]} 0px, transparent 50%),
    radial-gradient(at 100% 100%, ${theme.colors.primary[50]} 0px, transparent 50%);
  position: relative;
  overflow: hidden;
  scroll-margin-top: 80px;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: ${theme.gradients.professional};
    opacity: 0.8;
  }
  @media (max-width: 1024px) {
    padding: 5rem 2rem;
  }

  @media (max-width: 768px) {
    padding: 4.5rem 1.5rem;
  }

  @media (max-width: 480px) {
    padding: 3.5rem 1.25rem;
  }
`;

const CertificatesContainer = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 1rem;
  position: relative;

  @media (max-width: 768px) {
    padding: 0 0.5rem;
  }
`;

const SectionTitle = styled.h2`
  text-align: center;
  font-size: clamp(2rem, 4vw, 3rem);
  font-family: ${theme.typography.fontFamily.heading};
  font-weight: ${theme.typography.fontWeight.bold};
  margin-bottom: 4rem;
  color: ${theme.colors.neutral[900]};
  position: relative;
  animation: ${fadeIn} 0.6s ease-out;
  
  &::after {
    content: '';
    position: absolute;
    bottom: -12px;
    left: 50%;
    transform: translateX(-50%);
    width: 100px;
    height: 4px;
    background: ${theme.gradients.professional};
    border-radius: ${theme.borderRadius.full};
  }
`;

const CarouselWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  gap: 2rem;
  padding: 2rem 0;

  @media (max-width: 968px) {
    gap: 1rem;
  }
`;

const NavigationButton = styled.button`
  position: absolute;
  ${props => props.direction === 'left' ? 'left: -70px;' : 'right: -70px;'}
  top: 50%;
  transform: translateY(-50%);
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background: ${theme.gradients.professional};
  border: none;
  color: white;
  font-size: 1.75rem;
  font-weight: ${theme.typography.fontWeight.bold};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: ${theme.shadows.lg};
  transition: all ${theme.transitions.base};
  z-index: 10;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background: linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent);
    transform: translateX(-100%);
    transition: transform 0.6s;
  }
  
  &:hover:not(:disabled) {
    transform: translateY(-50%) scale(1.1);
    box-shadow: ${theme.shadows.xl};
    
    &::before {
      transform: translateX(100%);
    }
  }
  
  &:active:not(:disabled) {
    transform: translateY(-50%) scale(0.95);
  }
  
  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
    background: ${theme.colors.neutral[300]};
  }

  @media (max-width: 1200px) {
    ${props => props.direction === 'left' ? 'left: -10px;' : 'right: -10px;'}
    width: 50px;
    height: 50px;
    font-size: 1.5rem;
  }

  @media (max-width: 768px) {
    width: 44px;
    height: 44px;
    font-size: 1.25rem;
    ${props => props.direction === 'left' ? 'left: -5px;' : 'right: -5px;'}
  }
`;

const CertificatesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  grid-template-rows: repeat(2, 1fr);
  gap: 2rem;
  width: 100%;
  animation: ${fadeIn} 0.4s ease-out;
  
  @media (max-width: 1200px) {
    gap: 1.75rem;
  }
  
  @media (max-width: 1024px) {
    grid-template-columns: repeat(2, 1fr);
    grid-template-rows: repeat(3, 1fr);
  }
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    grid-template-rows: auto;
    gap: 1.5rem;
  }

  @media (max-width: 480px) {
    gap: 1.25rem;
  }
`;

const CertificateCard = styled.div`
  background: white;
  border-radius: ${theme.borderRadius.xl};
  padding: 0;
  box-shadow: ${theme.shadows.md};
  transition: all ${theme.transitions.slow};
  border: 1px solid ${theme.colors.neutral[200]};
  position: relative;
  overflow: hidden;
  cursor: pointer;
  aspect-ratio: 16/9;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: ${theme.gradients.professional};
    transform: scaleX(0);
    transform-origin: left;
    transition: transform ${theme.transitions.slow};
  }
  
  &::after {
    content: '';
    position: absolute;
    inset: 0;
    border-radius: ${theme.borderRadius.xl};
    padding: 2px;
    background: ${theme.gradients.professional};
    -webkit-mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
    -webkit-mask-composite: xor;
    mask-composite: exclude;
    opacity: 0;
    transition: opacity ${theme.transitions.base};
  }
  
  &:hover {
    transform: translateY(-12px) scale(1.02);
    box-shadow: ${theme.shadows['2xl']};
    border-color: transparent;
    
    &::before {
      transform: scaleX(1);
    }
    
    &::after {
      opacity: 1;
    }
  }

  @media (hover: none) {
    &:hover {
      transform: none;
      box-shadow: ${theme.shadows.md};
    }
  }
`;

const CertificateImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: ${theme.borderRadius.xl};
  transition: transform ${theme.transitions.slow};
  background: ${theme.colors.neutral[100]};
  
  ${CertificateCard}:hover & {
    transform: scale(1.05);
  }
`;

const ImagePlaceholder = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${theme.colors.neutral[100]};
  border-radius: ${theme.borderRadius.xl};
  
  &::after {
    content: '';
    width: 40px;
    height: 40px;
    border: 3px solid ${theme.colors.primary[200]};
    border-top-color: ${theme.colors.primary[600]};
    border-radius: 50%;
    animation: spin 0.8s linear infinite;
  }
  
  @keyframes spin {
    to { transform: rotate(360deg); }
  }
`;

const CertificateOverlay = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  background: linear-gradient(to top, rgba(0,0,0,0.95), rgba(0,0,0,0.7) 70%, transparent);
  padding: 2rem 1.25rem 1.25rem;
  transform: translateY(100%);
  transition: transform ${theme.transitions.base};
  pointer-events: none;
  
  ${CertificateCard}:hover & {
    transform: translateY(0);
  }

  @media (hover: none) {
    transform: translateY(0);
    padding: 1.5rem 1rem 1rem;
    background: linear-gradient(to top, rgba(0,0,0,0.85), rgba(0,0,0,0.5) 70%, transparent);
  }

  @media (max-width: 480px) {
    padding: 1.25rem 0.875rem 0.875rem;
  }
`;

const CertificateTitle = styled.h3`
  font-size: ${theme.typography.fontSize.base};
  font-weight: ${theme.typography.fontWeight.semibold};
  font-family: ${theme.typography.fontFamily.heading};
  color: #ffffff;
  margin: 0 0 0.5rem 0;
  line-height: 1.4;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;

  @media (max-width: 480px) {
    font-size: ${theme.typography.fontSize.sm};
    margin-bottom: 0.375rem;
  }
`;

const CertificateDate = styled.p`
  color: #60a5fa;
  font-size: ${theme.typography.fontSize.sm};
  font-weight: ${theme.typography.fontWeight.medium};
  margin: 0;
  opacity: 0.9;
`;

const PaginationInfo = styled.div`
  text-align: center;
  margin-top: 2.5rem;
  color: ${theme.colors.neutral[700]};
  font-size: ${theme.typography.fontSize.lg};
  font-weight: ${theme.typography.fontWeight.medium};
  
  span {
    color: ${theme.colors.primary[600]};
    font-weight: ${theme.typography.fontWeight.bold};
    font-size: ${theme.typography.fontSize.xl};
  }

  @media (max-width: 768px) {
    font-size: ${theme.typography.fontSize.base};
    margin-top: 2rem;
    
    span {
      font-size: ${theme.typography.fontSize.lg};
    }
  }

  @media (max-width: 480px) {
    font-size: ${theme.typography.fontSize.sm};
    margin-top: 1.5rem;
    
    span {
      font-size: ${theme.typography.fontSize.base};
    }
  }
`;

const PaginationDots = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 0.75rem;
  margin-top: 1.5rem;

  @media (max-width: 768px) {
    gap: 0.5rem;
  }
`;

const Dot = styled.button`
  width: 12px;
  height: 12px;
  border-radius: 50%;
  border: none;
  background: ${props => props.$active 
    ? theme.gradients.professional 
    : theme.colors.neutral[300]};
  cursor: pointer;
  transition: all ${theme.transitions.base};
  box-shadow: ${props => props.$active ? theme.shadows.sm : 'none'};
  
  &:hover {
    background: ${props => props.$active 
      ? theme.gradients.professional 
      : theme.colors.neutral[400]};
    transform: scale(1.3);
  }

  @media (max-width: 768px) {
    width: 10px;
    height: 10px;
  }
`;

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.9);
  backdrop-filter: blur(8px);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: ${theme.zIndex.modal};
  padding: 2rem;
  animation: ${fadeIn} 0.3s ease-out;
`;

const ModalContent = styled.div`
  position: relative;
  max-width: 95%;
  max-height: 90vh;
  animation: ${fadeIn} 0.4s ease-out 0.1s both;
  
  img {
    max-width: 100%;
    max-height: 90vh;
    border-radius: ${theme.borderRadius.xl};
    box-shadow: ${theme.shadows['2xl']};
    border: 2px solid rgba(59, 130, 246, 0.3);
  }
`;

const CloseButton = styled.button`
  position: absolute;
  top: -50px;
  right: 0;
  width: 40px;
  height: 40px;
  background: ${theme.gradients.professional};
  border: none;
  border-radius: 50%;
  color: white;
  font-size: 1.75rem;
  line-height: 1;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: ${theme.shadows.lg};
  transition: all ${theme.transitions.base};
  
  &:hover {
    transform: scale(1.1) rotate(90deg);
    box-shadow: ${theme.shadows.xl};
  }
  
  @media (max-width: 768px) {
    top: -60px;
    right: 50%;
    transform: translateX(50%);
    
    &:hover {
      transform: translateX(50%) scale(1.1) rotate(90deg);
    }
  }
`;

const KeyboardHint = styled.div`
  text-align: center;
  margin-top: 1rem;
  color: ${theme.colors.neutral[500]};
  font-size: ${theme.typography.fontSize.sm};
  opacity: 0.7;
  
  @media (max-width: 768px) {
    display: none;
  }
`;

const Modal = ({ image, onClose }) => {
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [onClose]);

  return createPortal(
    <ModalOverlay onClick={onClose}>
      <ModalContent onClick={e => e.stopPropagation()}>
        <CloseButton onClick={onClose}>&times;</CloseButton>
        <img src={image} alt="Certificate" />
      </ModalContent>
    </ModalOverlay>,
    document.body
  );
};

function Certificates() {
  const [selectedImage, setSelectedImage] = useState(null);
  const [currentPage, setCurrentPage] = useState(0);
  const [touchStart, setTouchStart] = useState(0);
  const [touchEnd, setTouchEnd] = useState(0);
  const [itemsPerPage, setItemsPerPage] = useState(6); // default desktop layout

  const openModal = useCallback((image) => {
    setSelectedImage(image);
    document.body.style.overflow = 'hidden';
  }, []);

  const closeModal = useCallback(() => {
    setSelectedImage(null);
    document.body.style.overflow = 'unset';
  }, []);

  // You can add your certificates here
  const certificates = [
    {
      title: "NASA International Space Apps Challenge - Galactic Problem Solver",
      date: "October 2023",
      description: "Recognized for outstanding participation and efforts to solve challenges on Earth and in space during the NASA International Space Apps Challenge.",
      image: "/certificates/nasa.png"
    },
    {
      title: "Certificate of Appreciation - Flutter Workshop",
      date: "February 2023",
      description: "Attended a Flutter workshop at GDG RandoTek V2, focusing on mobile app development.",
      image: "/certificates/google.png"
    },
    {
      title: "Certificate of Appreciation - Club President",
      date: "2023-2025",
      description: "Recognized for serving as Club President of TryIt ISIMA, acknowledging leadership and contribution during the term.",
      image: "/certificates/tryit.png"
    },
    {
      title: "PMI Project Management Course",
      date: "February 2025",
      description: "Certificate of participation in PMI's Project Management Course.",
      image: "/certificates/initiateur_mstr.png" 
    },
    {
      title: "Robotics Club Mentor Appreciation",
      date: "February 2025",
      description: "Recognition for engagement and support in mentoring participants in robotics clubs.",
      image: "/certificates/pnl.png" 
    },
    {
      title: "ISIMA Organizational Committee Appreciation",
      date: "February 2025",
      description: "Certificate of gratitude for significant efforts and dedication to the success of an event.",
      image: "/certificates/isima.png" 
    },
    {
      title: "TRY-IT Club Recognition",
      date: "February 2025",
      description: "Certificate of recognition for the TRY-IT ISIMA Club's significant contribution to the institute community.",
      image: "/certificates/tryit_club.png" 
    },
    {
      title: "Innovative Initiatives Competition Finalist",
      date: "June 2024",
      description: "Participation certificate in the final of 'The Best Innovative and Entrepreneurial Initiatives' competition.",
      image: "/certificates/monaastir_comp.png" 
    },
    {
      title: "WOLF BOT V1.0 Challenge Participation",
      date: "April 2025",
      description: "Certificate for participating in the WOLF BOT V1.0 Challenge, organized by Cyberobot.",
      image: "/certificates/wolfbot.png" 
    },
    {
      title: "Internship Certificate - JPartners Tunisie",
      date: "March 2023",
      description: "Attestation of internship completed at JPartners Tunisie SUARL.",
      image: "/certificates/stage_sans.png" 
    },
    {
      title: "Internship Certificate - Digi Growing",
      date: "July 2025",
      description: "Internship at Digi Growing focused on developing the NeoRover autonomous vehicle: embedded systems, motor control, and ROS2 integration.",
      image: "/certificates/Attestation_de_stage.png"
    }    
    // Add more certificates as needed
  ];

  // Calculate pagination with useMemo for performance
  const { totalPages, startIndex, currentCertificates } = useMemo(() => {
    const total = Math.ceil(certificates.length / itemsPerPage);
    const start = currentPage * itemsPerPage;
    const end = start + itemsPerPage;
    const current = certificates.slice(start, end);
    
    return {
      totalPages: total,
      startIndex: start,
      currentCertificates: current
    };
  }, [certificates.length, currentPage, itemsPerPage]);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;

    const determineItemsPerPage = () => {
      const width = window.innerWidth;

      if (width < 640) {
        setItemsPerPage(3);
      } else if (width < 1024) {
        setItemsPerPage(4);
      } else {
        setItemsPerPage(6);
      }
    };

    determineItemsPerPage();
    window.addEventListener('resize', determineItemsPerPage);

    return () => {
      window.removeEventListener('resize', determineItemsPerPage);
    };
  }, []);

  useEffect(() => {
    const total = Math.max(0, Math.ceil(certificates.length / itemsPerPage) - 1);
    setCurrentPage(prev => Math.min(prev, total));
  }, [itemsPerPage, certificates.length]);

  const goToNextPage = useCallback(() => {
    setCurrentPage(prev => (prev < totalPages - 1 ? prev + 1 : prev));
  }, [totalPages]);

  const goToPrevPage = useCallback(() => {
    setCurrentPage(prev => (prev > 0 ? prev - 1 : prev));
  }, []);

  const goToPage = useCallback((pageIndex) => {
    setCurrentPage(pageIndex);
  }, []);

  // Touch swipe handlers for mobile
  const handleTouchStart = (e) => {
    setTouchStart(e.targetTouches[0].clientX);
  };

  const handleTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const handleTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > 50;
    const isRightSwipe = distance < -50;
    
    if (isLeftSwipe && currentPage < totalPages - 1) {
      goToNextPage();
    }
    
    if (isRightSwipe && currentPage > 0) {
      goToPrevPage();
    }
    
    setTouchStart(0);
    setTouchEnd(0);
  };

  // Keyboard navigation
  useEffect(() => {
    const handleKeyPress = (e) => {
      if (e.key === 'ArrowLeft') {
        setCurrentPage(prev => (prev > 0 ? prev - 1 : prev));
      } else if (e.key === 'ArrowRight') {
        setCurrentPage(prev => (prev < totalPages - 1 ? prev + 1 : prev));
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [totalPages]);

  return (
    <CertificatesSection id="certificates">
      <CertificatesContainer>
        <ScrollReveal direction="bounce" duration="1s">
          <SectionTitle>Certificates & Achievements</SectionTitle>
        </ScrollReveal>
        
        <ScrollReveal direction="blur" delay="0.15s" duration="0.9s">
        <CarouselWrapper>
          <NavigationButton 
            direction="left" 
            onClick={goToPrevPage}
            disabled={currentPage === 0}
            aria-label="Previous page"
            title="Previous (←)"
          >
            ‹
          </NavigationButton>

          <CertificatesGrid
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
          >
            {currentCertificates.map((cert, index) => (
              <CertificateCard 
                key={startIndex + index}
                onClick={() => openModal(cert.image)}
                role="button"
                tabIndex={0}
                aria-label={`View ${cert.title}`}
                onKeyPress={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    openModal(cert.image);
                  }
                }}
              >
                <CertificateImage 
                  src={cert.image} 
                  alt={cert.title} 
                  loading="lazy"
                />
                <CertificateOverlay>
                  <CertificateTitle>{cert.title}</CertificateTitle>
                  <CertificateDate>{cert.date}</CertificateDate>
                </CertificateOverlay>
            </CertificateCard>
          ))}
        </CertificatesGrid>

          <NavigationButton 
            direction="right" 
            onClick={goToNextPage}
            disabled={currentPage === totalPages - 1}
            aria-label="Next page"
            title="Next (→)"
          >
            ›
          </NavigationButton>
        </CarouselWrapper>

        <PaginationInfo>
          Page <span>{currentPage + 1}</span> of <span>{totalPages}</span>
        </PaginationInfo>

        <PaginationDots>
          {Array.from({ length: totalPages }).map((_, index) => (
            <Dot
              key={index}
              $active={index === currentPage}
              onClick={() => goToPage(index)}
              aria-label={`Go to page ${index + 1}`}
              aria-current={index === currentPage ? 'page' : undefined}
            />
          ))}
        </PaginationDots>

        <KeyboardHint>
          Use arrow keys (← →) to navigate • Swipe on mobile
        </KeyboardHint>
        </ScrollReveal>
      </CertificatesContainer>
      
      {selectedImage && <Modal image={selectedImage} onClose={closeModal} />}
    </CertificatesSection>
  );
}

export default Certificates;
